'use client'

import * as TabsPrimitive from '@radix-ui/react-tabs'
import { cn } from '@workspace/ui/lib/utils'
import { useCallback, useEffect, useRef, useState } from 'react'

interface ScrollableTabsListProps extends React.ComponentProps<typeof TabsPrimitive.List> {
  /** Background color for gradient fade. Use 'background' for page level, 'card' for card containers */
  gradientFrom?: 'background' | 'card'
}

function ScrollableTabsList({
  className,
  children,
  gradientFrom = 'background',
  ...props
}: ScrollableTabsListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const [leftOpacity, setLeftOpacity] = useState(0)
  const [rightOpacity, setRightOpacity] = useState(0)

  const updateScrollState = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    if (maxScroll <= 0) {
      setLeftOpacity(0)
      setRightOpacity(0)
      return
    }
    const fadeDistance = 50
    setLeftOpacity(Math.min(el.scrollLeft / fadeDistance, 1))
    setRightOpacity(Math.min((maxScroll - el.scrollLeft) / fadeDistance, 1))
  }, [])

  const scrollToCenter = useCallback((target: HTMLElement) => {
    const container = scrollRef.current
    if (!container) return
    const containerRect = container.getBoundingClientRect()
    const targetRect = target.getBoundingClientRect()
    const targetCenter = targetRect.left + targetRect.width / 2
    const containerCenter = containerRect.left + containerRect.width / 2
    const scrollOffset = targetCenter - containerCenter
    container.scrollBy({ left: scrollOffset, behavior: 'smooth' })
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollState()
    el.addEventListener('scroll', updateScrollState)
    window.addEventListener('resize', updateScrollState)
    return () => {
      el.removeEventListener('scroll', updateScrollState)
      window.removeEventListener('resize', updateScrollState)
    }
  }, [updateScrollState])

  const gradientClass = gradientFrom === 'card' ? 'from-card' : 'from-background'

  return (
    <div className="relative">
      <div ref={scrollRef} className="overflow-x-auto scrollbar-none">
        <TabsPrimitive.List
          data-slot="tabs-list"
          className={cn(
            'inline-flex h-10 w-max sm:w-auto items-center justify-center rounded-md bg-muted p-1 text-muted-foreground',
            className
          )}
          {...props}
        >
          {/* Clone children to inject scrollToCenter onClick */}
          {Array.isArray(children)
            ? children.map((child, index) => {
                if (!child || typeof child !== 'object' || !('props' in child)) return child
                return (
                  <child.type
                    key={child.key ?? index}
                    {...child.props}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      scrollToCenter(e.currentTarget)
                      child.props.onClick?.(e)
                    }}
                  />
                )
              })
            : children}
        </TabsPrimitive.List>
      </div>
      {/* Fade gradient on left edge */}
      <div
        className={cn(
          'absolute left-0 top-0 bottom-0 w-8 bg-linear-to-r to-transparent pointer-events-none sm:hidden',
          gradientClass
        )}
        style={{ opacity: leftOpacity }}
      />
      {/* Fade gradient on right edge */}
      <div
        className={cn(
          'absolute right-0 top-0 bottom-0 w-8 bg-linear-to-l to-transparent pointer-events-none sm:hidden',
          gradientClass
        )}
        style={{ opacity: rightOpacity }}
      />
    </div>
  )
}

export { ScrollableTabsList }
